import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Loader2, Upload } from "lucide-react";
import { useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";

type Source = "linkedin" | "json";

/**
 * Add-to-corpus drawer. Supports two input modes:
 *   1. LinkedIn URL — server fetches the profile via the existing
 *      LinkedInProfileAPI integration. Requires LINKEDIN_PROFILE_API_KEY
 *      in env or a fixture dir hit.
 *   2. Raw JSON — paste a previously-fetched LinkedInProfileRaw blob
 *      (e.g., from the `docs/labs/few-shot-candidates/` dir). Useful
 *      when the API is unavailable or for deterministic seeding.
 *
 * The fixture is stored with a server-generated opaque identifier; the UI
 * never asks for or stores a real candidate name.
 */
export function CorpusAddDrawer({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAdded: () => void;
}) {
  const { toast } = useToast();
  const [source, setSource] = useState<Source>("linkedin");
  const [hired, setHired] = useState<boolean>(true);
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [jsonBlob, setJsonBlob] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const formId = useId();

  const reset = () => {
    setLinkedinUrl("");
    setJsonBlob("");
    setHired(true);
    setSource("linkedin");
    setParseError(null);
  };

  const submit = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      const payload: {
        hired: boolean;
        expectedScores: Record<string, number>;
        profile?: unknown;
        linkedinUrl?: string;
      } = { hired, expectedScores: {} };
      if (source === "json") {
        try {
          payload.profile = JSON.parse(jsonBlob);
        } catch (err) {
          throw new Error(`JSON inválido: ${err instanceof Error ? err.message : "erro de parse"}`);
        }
      } else {
        const url = linkedinUrl.trim();
        if (!url) throw new Error("Informe a URL do LinkedIn");
        payload.linkedinUrl = url;
      }
      return rpcClient.labs.calibrationCorpusCreate(payload);
    },
    onSuccess: () => {
      reset();
      onAdded();
      onOpenChange(false);
      toast({
        title: "Perfil adicionado",
        description: "Lembre-se de rotular as notas esperadas antes do próximo ajuste.",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao adicionar", description: err.message, variant: "destructive" });
    },
  });

  const handleFileUpload = async (file: File) => {
    setParseError(null);
    try {
      const text = await file.text();
      JSON.parse(text); // validate up front so we surface format errors immediately
      setJsonBlob(text);
      setSource("json");
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "arquivo não é JSON válido");
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto">
        <SheetHeader className="space-y-2">
          <SheetTitle className="text-2xl font-bold tracking-tight">
            Adicionar perfil ao corpus
          </SheetTitle>
          <SheetDescription className="text-xs leading-relaxed">
            O servidor guarda o payload completo do LinkedIn (nome, carreira, formação) — é o que o
            modelo lê na hora de pontuar. Você rotula as notas esperadas depois, direto na tabela do
            corpus.
          </SheetDescription>
        </SheetHeader>

        <form
          id={formId}
          className="mt-6 space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            submit.mutate();
          }}
        >
          <fieldset className="space-y-3">
            <legend className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              Contratamos esta pessoa?
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <HiredToggle active={hired === true} onClick={() => setHired(true)} label="Sim" />
              <HiredToggle active={hired === false} onClick={() => setHired(false)} label="Não" />
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              Fonte do perfil
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <SourceToggle
                active={source === "linkedin"}
                onClick={() => setSource("linkedin")}
                label="URL do LinkedIn"
                hint="o servidor busca via API"
              />
              <SourceToggle
                active={source === "json"}
                onClick={() => setSource("json")}
                label="JSON do perfil"
                hint="cole ou carregue arquivo"
              />
            </div>
          </fieldset>

          {source === "linkedin" ? (
            <div className="space-y-2">
              <label
                htmlFor={`${formId}-url`}
                className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground"
              >
                URL
              </label>
              <Input
                id={`${formId}-url`}
                type="url"
                placeholder="https://www.linkedin.com/in/..."
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                data-testid="corpus-add-url"
                required
              />
              <p className="text-xs text-muted-foreground">
                Requer uma <code>LINKEDIN_PROFILE_API_KEY</code> configurada no servidor ou um
                fixture prévio no diretório gitignored.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor={`${formId}-json`}
                  className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground"
                >
                  Payload
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-3 w-3 mr-1" />
                  carregar .json
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileUpload(f);
                  }}
                />
              </div>
              <Textarea
                id={`${formId}-json`}
                rows={10}
                placeholder='{"firstName": "...", "lastName": "...", ...}'
                value={jsonBlob}
                onChange={(e) => {
                  setJsonBlob(e.target.value);
                  setParseError(null);
                }}
                className="font-mono text-xs"
                data-testid="corpus-add-json"
              />
              {parseError && (
                <p className="flex items-start gap-1.5 text-xs text-rose-600">
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>{parseError}</span>
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Formato esperado: <code>LinkedInProfileRaw</code> (mesmo shape do{" "}
                <code>docs/labs/few-shot-candidates/</code>).
              </p>
            </div>
          )}
        </form>

        <SheetFooter className="mt-6 border-t pt-4 gap-2 flex-col-reverse sm:flex-row">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submit.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={
              submit.isPending || (source === "linkedin" ? !linkedinUrl.trim() : !jsonBlob.trim())
            }
            data-testid="corpus-add-submit"
          >
            {submit.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
                Adicionando…
              </>
            ) : (
              "Adicionar ao corpus"
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function HiredToggle({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border px-4 py-3 text-left transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background hover:bg-muted/40"
      }`}
      data-testid={`corpus-add-hired-${label === "Sim" ? "yes" : "no"}`}
    >
      <div className="text-sm font-semibold">{label}</div>
      <div
        className={`text-[11px] ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}
      >
        {label === "Sim" ? "integra o corpus bom" : "integra o corpus ruim"}
      </div>
    </button>
  );
}

function SourceToggle({
  active,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border px-4 py-3 text-left transition-colors ${
        active ? "border-primary bg-muted/30" : "border-border bg-background hover:bg-muted/40"
      }`}
    >
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-[11px] text-muted-foreground">{hint}</div>
    </button>
  );
}
